<template>
  <HgLayout
    :baseUrl="baseUrl"
    :collapsibleStartUnfolded="false"
    :currentLanguage="$i18n.locale"
    :currentRoute="$route"
    :getLanguageToLink="getLanguageSwitchLink"
    :isWhiteLabelSolution="isLayoutSolutionEnabled"
    :isTopNavEnabled="isNewNavigationEnabled"
    :isUserLoggedIn="isPrivateUserLoggedIn"
    @trackFooterClick="handleTrackFooterClick"
    @trackHeaderClick="handleTrackHeaderClick"
  >
    <template #headerMetaAction>
      <HgMainNavItem
        :menuItem="
          tm(
            isNewNavigationEnabled
              ? '$hg.nav.topNavNewListingMenuItem'
              : '$hg.nav.harmonize.newListingMenuItem',
          )
        "
        :currentRoute="$route"
        :baseUrl="baseUrl"
        :bordered="true"
        @click="handleTrackNavItemClick('open_insertAd', 'tab_navigation')"
      />
    </template>
    <template #headerMetaNavigation>
      <FavouritesLink
        v-if="favouritesStore.isFavouritesEnabled"
        :menuItem="tm('$hg.nav.favoriteMenuItem')"
        :menuNavItem="HgMainNavItem"
        :platform="SupportedPlatforms.hg"
        :baseUrl="baseUrl"
        :route="$route"
        :storeHasUserFavourites="storeHasUserFavourites"
        @click="handleTrackNavItemClick('open_favourites', 'tab_navigation')"
      />
      <HgMainNavItem
        :baseUrl="baseUrl"
        icon="PersonOutline"
        :menuItem="loggedInUserMenuItem"
        :currentRoute="$route"
        dropdownOrientation="right"
        @mainNavItemClick="handleMainNavTracking"
        @dropDownMenuItemClick="handleMainNavTracking"
      >
      </HgMainNavItem>
    </template>
    <template #sideMenuMetaNavigation>
      <FavouritesLink
        v-if="favouritesStore.isFavouritesEnabled"
        :menuItem="tm('$hg.nav.favoriteMenuItem')"
        :menuNavItem="HgSideMenuNavItem"
        :platform="SupportedPlatforms.hg"
        :baseUrl="baseUrl"
        :route="$route"
        :storeHasUserFavourites="storeHasUserFavourites"
        @click="handleTrackNavItemClick('open_favourites', 'tab_navigation')"
      />
    </template>
    <template #sideMenuAccountNavigation>
      <template v-if="isPrivateUserLoggedIn">
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.overviewMenuItem')"
          icon="PersonOutline"
          :currentRoute="$route"
          :baseUrl="accountsDomainOrigin"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.settingsMenuItem')"
          icon="Settings"
          :currentRoute="$route"
          :baseUrl="accountsDomainOrigin"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.myAdsMenuItem')"
          icon="Appointment"
          :currentRoute="$route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.myRealEstateMenuItem')"
          icon="House"
          :currentRoute="$route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.searchAlertMenuItem')"
          icon="Bell"
          :currentRoute="$route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.tenantPlusMenuItem')"
          icon="PlusMembership"
          :currentRoute="$route"
          :baseUrl="baseUrl"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.signOutMenuItem')"
          icon="SignOut"
          :currentRoute="$route"
          :baseUrl="accountsDomainOrigin"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
      </template>
      <template v-else>
        <HgSideMenuNavItem
          :menuItem="tm('$hg.nav.harmonize.signInMenuItem')"
          icon="PersonOutline"
          :currentRoute="$route"
          :baseUrl="accountsDomainOrigin"
          @sideMenuItemClick="handleMobileMenuNavTracking"
        />
      </template>
    </template>
    <div class="layout-container">
      <div class="row layout-solution-header">
        <div v-if="shouldShowLayoutSolutionElements" class="col back-in-history-link">
          <a href="#" @click="goBackInHistory">{{ t('back') }}</a>
        </div>
      </div>
      <router-view />
      <div v-if="shouldShowLayoutSolutionElements" class="powered-by">
        <span>powered by</span> <a href="/"><LogoHg class="logo" /></a>
      </div>
    </div>
  </HgLayout>
</template>

<script lang="ts" setup>
import { isPrivateLoggedInUser } from '@/helpers/Authentication/authClient';
import { getBasicPageViewTrackingContext, storeLanguageInCookie } from '@/helpers/GTM/GTMHelper';
import { getAppMetaInfo } from '@/helpers/MetaInfo/MetaInfoHelper';
import initIframeResizer from '@/helpers/initIframeResizer';
import {
  getLayoutSolutionRequest,
  isLayoutSolutionActive,
} from '@/layout-solution/layout-solution';
import { initializeFavouritesStore, useFavouritesStore } from '@/store/modules/favourites';
import {
  HgLayout,
  HgMainNavItem,
  HgSideMenuNavItem,
  HgSupportedLanguage,
  LogoHg,
  MenuItem,
  SupportedPlatforms,
} from '@smg-real-estate/domus';
import { FavouritesLink } from '@smg-real-estate/seeker-app-shared-components';
import { type SmgSplitSession } from '@smg-real-estate/vue-experiments';
import { useHead } from '@unhead/vue';
import {
  computed,
  onBeforeMount,
  onMounted,
  onServerPrefetch,
  ref,
  useSSRContext,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { GTMPageType } from './helpers/GTM/GTMParameters';
import { trackFooterClickHandler, trackHeaderClickHandler } from './helpers/GTM/GTMTrack';
import { normalizedFullPath } from './helpers/Router/RouterHelper';
import { fetchSmgSplitSession } from './splitio/splitApi';
import { SplitFeatureFlags } from './splitio/splitFeatureFlags';
import { useLayoutSolutionStore } from './store/modules/layout-solution';
import { useSplitStore } from './store/modules/split';
import { HeaderTrackingData, LayoutSolutionRequest, TrackingData } from './types';

const route = useRoute();
const router = useRouter();
const ssrContext = import.meta.env.SSR ? useSSRContext() : undefined;
const { t, tm, locale } = useI18n();
const layoutSolutionStore = useLayoutSolutionStore();
const splitStore = useSplitStore();
const favouritesStore = useFavouritesStore();

defineOptions({
  name: 'App',
});

const baseUrl = import.meta.env.VUE_APP_BASE_URL as string;
const accountsDomainOrigin = import.meta.env.VUE_APP_ACCOUNT_BASE_URL;

const isPrivateUserLoggedIn = ref(false);

const isLayoutSolutionEnabled = computed<boolean>(() => layoutSolutionStore.isActive);
const layoutSolutionRequest = computed<LayoutSolutionRequest | undefined>(() =>
  getLayoutSolutionRequest(route.query),
);
const shouldShowLayoutSolutionElements = computed<boolean>(
  () => isLayoutSolutionEnabled.value && !layoutSolutionRequest.value?.hideLayoutElements,
);
const currentLanguage = computed<HgSupportedLanguage>(() => locale.value as HgSupportedLanguage);
const storeHasUserFavourites = computed<boolean | undefined>(() => {
  if (!favouritesStore.isFavouritesInitialized) {
    return undefined;
  }

  return favouritesStore.favouriteListingDescriptors.length > 0;
});
const currentNormalizedPath = computed<string>(() => normalizedFullPath(route, router));
const isNewNavigationEnabled = computed<boolean>(() =>
  splitStore.isTreatmentOn(SplitFeatureFlags.DD4533_NEW_TOP_LEVEL_NAVIGATION),
);
const myAccountDropdown = computed<MenuItem>(() => {
  const navItemsUsingBaseUrl = ['myAds', 'myRealEstate', 'searchAlertSettings', 'tenantPlus'];
  let privateItems = JSON.parse(JSON.stringify(tm('$hg.nav.privateItems')));

  if (Array.isArray(privateItems)) {
    privateItems = privateItems.map((item) => {
      return {
        ...item,
        href: navItemsUsingBaseUrl.includes(item.trackingLabel)
          ? `${baseUrl}${item.href}`
          : `${accountsDomainOrigin}${item.href}`,
      };
    });
  }
  return {
    label: t('$hg.nav.loggedInMenuItem.label'),
    children: privateItems,
  };
});
const userAccountMenuItem = computed<MenuItem>(() => {
  return {
    label: tm('$hg.nav.harmonize.overviewMenuItem.label') as string,
    href: `${import.meta.env.VUE_APP_ACCOUNT_BASE_URL}/${currentLanguage.value}/overview`,
  };
});
const loggedInUserMenuItem = computed<MenuItem>(() => {
  return isPrivateUserLoggedIn.value ? myAccountDropdown.value : userAccountMenuItem.value;
});

layoutSolutionStore.setIsLayoutSolutionActive(isLayoutSolutionActive(route.query));

useHead(getAppMetaInfo('Homegate', currentLanguage.value));

watch(currentLanguage, (to, from) => {
  if (to !== from && !isLayoutSolutionEnabled.value) {
    storeLanguageInCookie(to);
  }
});

onMounted(async () => {
  isPrivateUserLoggedIn.value = await isPrivateLoggedInUser();

  initializeFavouritesStore(route);

  window.addEventListener('optanonHandler', async () => {
    const renderPath = window.location.pathname;
    const smgSplitSessions = await fetchSmgSplitSession(renderPath);
    splitStore.updateSplitStoreFromApi(smgSplitSessions);
  });
});
onBeforeMount(() => {
  if (isLayoutSolutionEnabled.value) {
    initIframeResizer();
  } else {
    storeLanguageInCookie(currentLanguage.value);
  }
});
const handleTrackNavItemClick = (action: string, label: string) => {
  const trackingData = {
    eventAction: action,
    eventLabel: label,
    contentLanguage: currentLanguage.value,
  };
  trackHeaderClickHandler(trackingData);
};
const handleTrackFooterClick = (data: Omit<TrackingData, 'eventInteraction'>) => {
  trackFooterClickHandler(data);
};
const handleTrackHeaderClick = (data: HeaderTrackingData) => {
  trackHeaderClickHandler(data);
};
const handleNavTracking = (menuItem: MenuItem, event: Event, isMobile = false) => {
  handleTrackHeaderClick({
    eventAction: (menuItem as any).trackingLabel || 'open_myAccount',
    eventLabel: isMobile ? 'sidebar' : 'tab_navigation',
    contentLanguage: currentLanguage.value,
  });
};
const handleMainNavTracking = (menuItem: MenuItem, event: Event) => {
  handleNavTracking(menuItem, event, false);
};
const handleMobileMenuNavTracking = (menuItem: MenuItem, event: Event) => {
  handleNavTracking(menuItem, event, true);
};
const getLanguageSwitchLink = (language: HgSupportedLanguage) => {
  if (typeof route.meta?.getLanguageSwitchLink === 'function') {
    return route.meta?.getLanguageSwitchLink(route, language);
  }
};
const goBackInHistory = (event: Event): void => {
  event.preventDefault();
  if (history) {
    history.back();
  }
};
onServerPrefetch(() => {
  if (!ssrContext) {
    return;
  }

  let { smgSplitSession } = ssrContext as {
    smgSplitSession: SmgSplitSession;
  };
  splitStore.updateSplitStoreFromApi(smgSplitSession);

  ssrContext.pageTrackingContext = JSON.stringify({
    ...getBasicPageViewTrackingContext(
      route.meta.gtmPageType as GTMPageType,
      currentLanguage.value as HgSupportedLanguage,
      route.fullPath,
      currentNormalizedPath.value ?? null,
    ),
  });
});
</script>

<style lang="scss">
@import '@/assets/globals-hg';
</style>

<style lang="scss" scoped>
@import '@/assets/variables';

.layout-solution-header {
  align-items: center;
}

.back-in-history-link {
  padding-top: 1em;
  margin-top: 1em;
  text-align: right;
}

.powered-by {
  margin-top: 1em;
  margin-bottom: 1em;
  text-align: right;
  vertical-align: center;

  span {
    position: relative;
    top: -4px;
    font-size: 12px;
  }

  a {
    .logo {
      display: inline-block;
      width: 120px;
    }
  }
}
</style>
